import { FC, useMemo } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { Trans, useTranslation } from '@frontend/i18n';
import { Text, TextLink } from '@frontend/design-system';
import { PromoBanner } from '../../../common/components/promo-banners';
import { TSS_RECOVERED_OPPORTUNITY_HELP_LINK } from '../../constants';
import { useCallIntelInAppBannerVisibility } from '../../hooks/use-call-intel-in-app-banner-visibility';
import { getInAppAwarenessBannerTrackingId } from '../../tracking-ids';
import { InAppAwarenessPromoSource } from '../../types';

interface TSSRecoveredOpportunityBannerProps {
  styles?: Interpolation<Theme>;
  locationId?: string;
  isDemo?: boolean;
  callInsights?: Pick<CallIntelTypes.CallRecord, 'schedulingOutcomeV2' | 'isFieldEdited'>;
}

const COMPONENT_ID: InAppAwarenessPromoSource = 'call-insight-recovered-opportunity';

export const TSSRecoveredOpportunityBanner: FC<TSSRecoveredOpportunityBannerProps> = ({
  styles,
  locationId,
  isDemo,
  callInsights,
}) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'call-intel' });
  const bannerLocationIds = useMemo(() => (locationId ? [locationId] : []), [locationId]);

  const { ref, handleDismiss, shouldShowBanner, isBetaBanner } = useCallIntelInAppBannerVisibility({
    locationIds: bannerLocationIds,
    isDemo,
    componentId: COMPONENT_ID,
    enabled:
      !!callInsights &&
      !callInsights.isFieldEdited?.isSchedulingOutcomeEdited &&
      callInsights.schedulingOutcomeV2 ===
        CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL,
    maxVisibilityCount: 1,
    maxInteractionCount: 0,
  });

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <PromoBanner
      badge={isBetaBanner ? 'beta' : 'new'}
      containerStyles={styles}
      innerRef={ref}
      title={
        <Text size='medium'>
          {/* Learn more links are not ready for beta yet */}
          {isBetaBanner ? (
            <Trans t={t}>
              <strong>Unscheduled</strong> calls are automatically updated to <strong>Recovered Opportunity</strong>{' '}
              when an appointment is scheduled within 7 days after the call.
            </Trans>
          ) : (
            <Trans t={t}>
              <strong>Unscheduled</strong> calls are automatically updated to <strong>Recovered Opportunity</strong>{' '}
              when an appointment is scheduled within 7 days after the call.{' '}
              <TextLink
                target='_blank'
                rel='noopener noreferrer'
                href={TSS_RECOVERED_OPPORTUNITY_HELP_LINK}
                trackingId={getInAppAwarenessBannerTrackingId({
                  source: 'call-insight-recovered-opportunity',
                  isBeta: isBetaBanner,
                  action: 'learn-more',
                })}
              >
                Learn More
              </TextLink>
            </Trans>
          )}
        </Text>
      }
      dismissTrackingId={getInAppAwarenessBannerTrackingId({
        source: COMPONENT_ID,
        isBeta: isBetaBanner,
        action: 'dismiss',
      })}
      onDismiss={handleDismiss}
      color={isBetaBanner ? 'indigo' : 'seaweed'}
      responsiveActions={false}
    />
  );
};
